<template>
    <div class="room-plan-view" :class="{ 'transfer-bill-mode': isTransferBillMode }">
        <section v-if="isTransferBillMode" class="transfer-bill-actions">
            <strong>
                {{ translations.roomPlan.selectTableToTransferBill }}
            </strong>
            <VButton class="cancel-bill-transfer-btn warning rounded" @click="cancelTransferBill">
                {{ translations.general.cancel }}
            </VButton>
        </section>

        <ZoomableContainer v-if="!isLoading && room != null" class="room-container" :defaultScale="defaultScale">
            <div class="room" :class="[roomShapeClass]">
                <RoomPlanTable
                    v-for="table of tablesToDisplay"
                    :key="table.id"
                    :table="table"
                    :disabled="isTableDisabled(table)"
                    @tableSelected="onTableSelected"
                />
            </div>
        </ZoomableContainer>

        <div v-if="isLoading" class="loading-container">
            <div class="loading">
                <div class="loader green"></div>
                {{ translations.roomPlan.loadingTables }}
            </div>
        </div>

        <section class="footer">
            <div v-if="currentRoomName" class="current">
                <span class="name"><bdi>{{ currentRoomName }}</bdi></span>

                <ChangeRoomList
                    :locationId="location!.id"
                    :currentRoomId="room!.id"
                    overlayTarget=".view.room-plan"
                    @roomSelected="onRoomSelected"
                />
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { BillDao } from "@/data/dao/service/BillDao";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import { RoomDao } from "@/data/dao/service/RoomDao";
    import { dataEvents } from "@/data/events/dataEvents";
    import { RoomShape } from "@/data/model/Enums";
    import type {
        IBillClosedEvent,
        IBillEvent,
        IBillManualEndUsersCountChangedEvent,
        IReservationTableAssignmentChangedEvent,
        IRoomPlanChangedEvent,
        ITableCovidUserCountChangedEvent,
    } from "@/data/model/Events";
    import type { ILocationBasicInfo } from "@/data/model/Location";
    import { type IRoomPlanTable } from "@/data/model/Operator";
    import type { IRoomWithService } from "@/data/model/Room";
    import type { TransferBillOptions } from "@/data/model/Table";
    import { type SideMenuChangedSelectedDateEventData, operatorEvents } from "@/operator/events/operatorEvents";
    import { getLocationName } from "@/shared/helpers/LocationHelper";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

    import VButton from "@/components/elements/VButton.vue";
    import ZoomableContainer from "@/components/layout/ZoomableContainer.vue";
    import ChangeRoomList from "@/components/roomPlan/ChangeRoomList.vue";
    import RoomPlanTable from "@/components/roomPlan/RoomPlanTable.vue";

    // define properties
    const props = defineProps<{
        transferBillOptions?: TransferBillOptions;
        scaleLandscapeView?: boolean;
    }>();

    const emit = defineEmits(["tableSelected", "multipleOpenBillsTableSelected"]);

    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);
    const roomDao = Factory.getInstance<RoomDao>(RoomDao);
    const billDao = Factory.getInstance<BillDao>(BillDao);

    const selectedReservationDate = ref<Date | undefined>(undefined);
    const changeRoomListComponent = ref<InstanceType<typeof ChangeRoomList> | null>(null);

    const location = ref<ILocationBasicInfo | undefined>(undefined);
    const room = ref<IRoomWithService | undefined>(undefined);
    const tables = ref<IRoomPlanTable[]>([]);

    const tablesToDisplay = computed<IRoomPlanTable[]>(() => tables.value.filter((t) => t.dimension != null));
    const currentRoomName = computed(() =>
        location.value != null && room.value != null ? `${getLocationName(location.value)} - ${room.value.name}` : null
    );
    const roomShapeClass = computed(() => {
        if (room.value?.shape == RoomShape.Landscape) return "landscape";
        if (room.value?.shape == RoomShape.Portrait) return "portrait";
        return "square";
    });
    const isLoading = ref<boolean>(false);
    const defaultScale = computed(() => {
        if (props.scaleLandscapeView && room.value?.shape == RoomShape.Landscape) {
            return 1;
            // return 1.5;
        }
        return 1;
    });

    watch(selectedReservationDate, async (newValue) => {
        console.log("New reservation date", newValue);

        if (room.value == null) return;

        await loadRoomPlan({
            location: location.value!,
            room: room.value!,
        });
    });

    function isTableDisabled(table: IRoomPlanTable) {
        return isTransferBillMode.value && table.id == transferBillOperationOptions.value?.currentTableId;
    }

    function reset() {
        location.value = undefined;
        room.value = undefined;
        tables.value = [];
    }

    async function loadRoomPlan(options: {
        location: ILocationBasicInfo;
        roomId?: string;
        room?: IRoomWithService;
    }): Promise<void> {
        if (options.roomId === undefined && options.room === undefined) {
            logError("RoomPlanViewComponent.vue loadRoomPlan: roomId and roomData are undefined");
            return;
        }

        isLoading.value = true;
        reset();

        try {
            location.value = options.location;
            room.value = options.room ?? (await roomDao.getSingle(options.roomId!));

            tables.value = await operatorDao.getRoomPlan({
                roomId: room.value!.id,
                reservationsDate: selectedReservationDate.value?.toISOString(),
            });
        } catch (ex) {
            logError(ex);
            reset();

            Popup.show({
                title: translations.general.error,
                message: translations.roomPlan.failedLoadingTables,
            });
        } finally {
            isLoading.value = false;
            await nextTick();

            if (room.value != null) {
                operatorEvents.roomPlanController.onRoomPlanLoaded.dispatch(room.value!.id);
            }
        }
    }

    function onRoomSelected(room: IRoomWithService) {
        loadRoomPlan({
            location: location.value!,
            room: room,
        });
    }

    function onTableSelected(table: IRoomPlanTable) {
        if (isTransferBillMode.value) {
            selectTableToTransferBill(table);
            return;
        }

        if (table.openBillsCount > 1) {
            emit("multipleOpenBillsTableSelected", table);
            return;
        }

        emit("tableSelected", table);
    }

    function onGuestNumberChanged(ev: ITableCovidUserCountChangedEvent) {
        //get table and update guests number
        const table = tables.value.find((t) => t.id == ev.tableId);

        if (table) {
            table.allocations = ev.covidUsersAtTable;
        }
    }

    function onManualEndUsersCountChanged(ev: IBillManualEndUsersCountChangedEvent) {
        //get table and update manual guests number
        const table = tables.value.find((t) => t.id == ev.tableId);

        if (table) {
            table.manualEndUsersCount = ev.manualEndUsersCount;
        }
    }

    function onBillClosed(ev: IBillClosedEvent) {
        //get table and update manual guests number
        const table = tables.value.find((t) => t.id == ev.tableId);

        if (table) {
            table.manualEndUsersCount = undefined;
        }
    }

    async function onTransferBillItemsDisabled(ev: IBillEvent) {
        if (transferBillOperationOptions.value != null && transferBillOperationOptions.value!.billId === ev.billId) {
            transferBillOperationOptions.value = undefined;
            await loadRoomPlan({ location: location.value!, roomId: room.value!.id });
        }
    }

    function onRoomPlanChanged(ev: IRoomPlanChangedEvent) {
        console.log("RoomPlan: RoomPlanChanged");
        // ignore if changed room is not the same as currently displayed
        if (ev.roomId && room.value?.id !== ev.roomId) {
            return;
        }

        loadRoomPlan({ location: location.value!, roomId: ev.roomId });
    }

    function onReservationTableAssignmentChanged(ev: IReservationTableAssignmentChangedEvent) {
        console.log("RoomPlan: onReservationTableAssignmentChanged");
        // ignore if changed room is not the same as currently displayed
        if (ev.assignedRoomId && room.value?.id !== ev.assignedRoomId) {
            return;
        }

        setTimeout(() => {
            loadRoomPlan({ location: location.value!, roomId: ev.assignedRoomId });
        }, 2000);
    }

    /* Transfer bill actions */
    const transferBillOperationOptions = ref(props.transferBillOptions);
    const isTransferBillMode = computed(() => transferBillOperationOptions.value != null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async function selectTableToTransferBill(table: IRoomPlanTable) {
        let isSuccess = false;
        try {
            isLoading.value = true;
            await billDao.transferBill({
                locationId: undefined,
                sourceBillId: transferBillOperationOptions.value!.billId,
                targetTableId: table.id,
                selectedItems:
                    transferBillOperationOptions.value!.selectedItems!.map((it) => {
                        return {
                            billItemId: it.billItemId,
                            quantity: it.quantity,
                        };
                    }) ?? [],
            });

            isSuccess = true;
        } catch {
            // empty
        } finally {
            transferBillOperationOptions.value = undefined;
            isLoading.value = false;

            if (isSuccess) {
                if (room.value != null) {
                    await loadRoomPlan({ location: location.value!, roomId: room.value.id });
                }
            }
        }
    }

    async function cancelTransferBill() {
        try {
            await Factory.getInstance<BillDao>(BillDao).disableTransferBillItems({
                billId: transferBillOperationOptions.value!.billId,
            });
            transferBillOperationOptions.value = undefined;
        } catch (error) {
            logError(error);
            Popup.show({
                title: translations.general.error,
                message: translations.general.operationFailed,
            });
        }
    }

    function getCurrentRoom() {
        return room.value;
    }

    function onChangedSelectedDate(ev: SideMenuChangedSelectedDateEventData) {
        selectedReservationDate.value = ev.selectedDate;
    }

    function onHideSideMenu() {
        selectedReservationDate.value = undefined;
    }

    onMounted(async () => {
        dataEvents.operatorSocket.TableCovidUserCountChanged.add(onGuestNumberChanged);
        dataEvents.operatorSocket.BillManualEndUsersCountChanged.add(onManualEndUsersCountChanged);
        dataEvents.operatorSocket.BillClosed.add(onBillClosed);
        dataEvents.operatorSocket.RoomPlanChanged.add(onRoomPlanChanged);
        dataEvents.operatorSocket.TransferBillItemsDisabled.add(onTransferBillItemsDisabled);
        dataEvents.operatorSocket.ReservationTableAssignmentChanged.add(onReservationTableAssignmentChanged);

        dataEvents.appSocket.TableCovidUserCountChanged.add(onGuestNumberChanged);
        dataEvents.appSocket.BillManualEndUsersCountChanged.add(onManualEndUsersCountChanged);
        dataEvents.appSocket.BillClosed.add(onBillClosed);
        dataEvents.appSocket.RoomPlanChanged.add(onRoomPlanChanged);
        dataEvents.appSocket.TransferBillItemsDisabled.add(onTransferBillItemsDisabled);

        operatorEvents.sideMenu.onChangedSelectedDate.add(onChangedSelectedDate);
        operatorEvents.sideMenu.onHide.add(onHideSideMenu);
    });

    onBeforeUnmount(() => {
        dataEvents.operatorSocket.TableCovidUserCountChanged.remove(onGuestNumberChanged);
        dataEvents.operatorSocket.BillManualEndUsersCountChanged.remove(onManualEndUsersCountChanged);
        dataEvents.operatorSocket.BillClosed.remove(onBillClosed);
        dataEvents.operatorSocket.RoomPlanChanged.remove(onRoomPlanChanged);
        dataEvents.operatorSocket.TransferBillItemsDisabled.remove(onTransferBillItemsDisabled);
        dataEvents.operatorSocket.ReservationTableAssignmentChanged.remove(onReservationTableAssignmentChanged);

        dataEvents.appSocket.TableCovidUserCountChanged.remove(onGuestNumberChanged);
        dataEvents.appSocket.BillManualEndUsersCountChanged.remove(onManualEndUsersCountChanged);
        dataEvents.appSocket.BillClosed.remove(onBillClosed);
        dataEvents.appSocket.RoomPlanChanged.remove(onRoomPlanChanged);
        dataEvents.appSocket.TransferBillItemsDisabled.add(onTransferBillItemsDisabled);

        operatorEvents.sideMenu.onChangedSelectedDate.remove(onChangedSelectedDate);
        operatorEvents.sideMenu.onHide.remove(onHideSideMenu);

        changeRoomListComponent.value?.close();
    });

    defineExpose({
        loadRoomPlan,
        getCurrentRoom,

        isTransferBillMode,
    });
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    .room-plan-view {
        display: flex;
        flex-direction: column;

        // as a flex item the default min-width: auto would let the not wrapping
        // location/room name in the footer stretch the view beyond the screen
        min-width: 0;

        .transfer-bill-actions {
            padding: 0.5em 16px;
            text-align: right;

            strong {
                color: colors.$fontColor;
                font-size: 16px;
                margin-right: 0.5em;
            }
        }

        .room-container {
            flex: 1 1 auto;
            position: relative;
            height: auto;

            .room {
                box-sizing: border-box;
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                margin: auto;
                max-width: 100%;
                max-height: 100%;
                aspect-ratio: 1 / 1;

                &.landscape {
                    aspect-ratio: 10 / 7;
                }

                &.portrait {
                    aspect-ratio: 10 / 13;
                }
            }
        }

        .loading-container {
            position: relative;
            flex: 1 1 auto;
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .footer {
            box-sizing: border-box;
            border-top: 1px solid #ccc;
            padding: 5px 0;

            > .current {
                color: colors.$fontColor;
                padding: 0 16px;
                height: 40px;
                line-height: 40px;
                font-weight: 600;
                font-size: 14px;
                text-align: right;

                display: flex;
                align-items: center;
                justify-content: flex-end;

                // the switch room button always stays visible on the right,
                // a too long location/room name is cut off on its left side.
                // direction: rtl is what moves the cut to the left - text-align is
                // ignored as soon as the line overflows - and the <bdi> around the
                // name keeps the text itself in the normal left to right order
                > .name {
                    min-width: 0;
                    direction: rtl;
                    overflow: hidden;
                    white-space: nowrap;
                }

                > .show-change-room-list {
                    flex: 0 0 auto;
                }
            }
        }
    }
</style>
